import type { ZodType } from 'zod'

/**
 * Gemeinsame Bausteine für alle HTTP-Aufrufe dieser Anwendung.
 *
 * Es gibt zwei Datenquellen mit unterschiedlichen Adressen und
 * unterschiedlichen Kopfzeilen: die Weltmaschine-API (`client.ts`) und
 * PostgREST (`postgrest/client.ts`). Gemeinsam haben sie das Zugriffstoken,
 * die Fehlertypen und die Prüfung der Antwort gegen ein Zod-Schema.
 *
 * Genau das steht hier. Wer eine dritte Quelle anbindet, baut auf dieser
 * Datei auf und kopiert nichts.
 */

/**
 * Fehler eines HTTP-Aufrufs, mit genug Kontext für eine hilfreiche Meldung.
 *
 * Die Weltmaschine hat zwei Eigenheiten, die man kennen muss:
 *  - 401 heißt "Token fehlt oder ist abgelaufen"
 *  - 403 kann zweierlei bedeuten: fehlende Rolle, oder das Modul ist für
 *    diesen Mandanten nicht freigeschaltet (TenantFeatureMiddleware).
 *    Im zweiten Fall steht der Grund im Textkörper.
 */
export class ApiError extends Error {
  readonly status: number
  readonly url: string
  readonly body: string

  constructor(status: number, url: string, body: string) {
    super(ApiError.describe(status, body))
    this.name = 'ApiError'
    this.status = status
    this.url = url
    this.body = body
  }

  private static describe(status: number, body: string): string {
    switch (status) {
      case 401:
        return 'Nicht angemeldet oder die Sitzung ist abgelaufen.'
      case 403:
        return body.trim() !== ''
          ? `Zugriff verweigert: ${body.trim()}`
          : 'Zugriff verweigert. Vermutlich fehlt die Rolle "all:write".'
      case 404:
        return 'Der Endpunkt oder der Datensatz existiert nicht.'
      case 500:
        return 'Fehler im Backend. Der Aufruf war korrekt, die Weltmaschine kam nicht damit klar.'
      default:
        return `Die API hat mit ${String(status)} geantwortet.`
    }
  }

  /** True, wenn ein erneuter Anmeldeversuch sinnvoll ist. */
  get isAuthProblem(): boolean {
    return this.status === 401
  }
}

/** Eine erfolgreiche Antwort, deren Inhalt vom zugesagten DTO abweicht. */
export class ApiContractError extends Error {
  readonly url: string
  readonly details: string

  constructor(url: string, details: string) {
    super('Die API-Antwort entspricht nicht dem erwarteten Datenvertrag.')
    this.name = 'ApiContractError'
    this.url = url
    this.details = details
  }
}

export type TokenProvider = () => string | undefined

let tokenProvider: TokenProvider = () => undefined

/**
 * Hinterlegt, woher die HTTP-Clients ihr Zugriffstoken bekommen.
 *
 * Wird einmal beim Start gesetzt (siehe ApiTokenBridge). Der Umweg über
 * eine Funktion ist Absicht: Das Token wird bei jedem Aufruf frisch
 * abgefragt, damit ein still erneuertes Token sofort greift und nicht
 * eine abgelaufene Kopie hängen bleibt.
 *
 * Ein Provider für beide Ziele. Backend und PostgREST prüfen dasselbe
 * Keycloak-Token, nur mit unterschiedlichen Ansprüchen daran: das Backend
 * die Rollen, PostgREST den Claim `db_role`.
 */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider
}

export function currentToken(): string | undefined {
  return tokenProvider()
}

/** Werte, die als Query-Parameter taugen. Arrays werden mehrfach angehängt. */
export type QueryValue = string | number | boolean | readonly string[] | undefined

export function appendQuery(url: string, query: Readonly<Record<string, QueryValue>>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item))
    } else {
      params.append(key, String(value))
    }
  }

  const queryString = params.toString()
  return queryString === '' ? url : `${url}?${queryString}`
}

export interface ReadOptions<T> {
  /** Prüft erfolgreiche JSON-Antworten, bevor sie Anwendungscode erreichen. */
  readonly schema?: ZodType<T>
  /**
   * Baut den Fehler für eine nicht erfolgreiche Antwort.
   *
   * PostgREST antwortet mit einem eigenen Fehlerformat und braucht andere
   * Meldungen als die Weltmaschine. Ohne diesen Haken stünde bei einem
   * abgelehnten Schreibversuch "vermutlich fehlt die Rolle all:write", und
   * das führt beim Suchen in die Irre.
   */
  readonly makeError?: (status: number, url: string, body: string) => Error
}

/**
 * Wertet eine Antwort aus: Fehler werfen, leere Antworten durchreichen,
 * JSON prüfen.
 *
 * Ein leerer Textkörper ist kein Vertragsbruch, sondern der Normalfall bei
 * 204 und bei manchen Weltmaschine-Endpunkten. Er wird zu `undefined`,
 * nicht zu einem Fehler.
 */
export async function readResponse<T>(
  response: Response,
  url: string,
  options: ReadOptions<T> = {},
): Promise<T> {
  const { schema, makeError } = options

  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw makeError
      ? makeError(response.status, url, body)
      : new ApiError(response.status, url, body)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  if (text.trim() === '') {
    return undefined as T
  }

  let json: unknown
  try {
    json = JSON.parse(text) as unknown
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Unbekannter JSON-Fehler'
    throw new ApiContractError(url, detail)
  }

  if (schema === undefined) return json as T

  const result = schema.safeParse(json)
  if (!result.success) {
    const details = result.error.issues
      .map((issue) => `${issue.path.join('.') || '<Antwort>'}: ${issue.message}`)
      .join('; ')
    throw new ApiContractError(url, details)
  }

  return result.data
}
